import { Routes, Route, Navigate } from "react-router-dom";

import AuthTabs from "./AuthTabs";
import SignUp from "./SignUp";
import ConfirmSignUp from "./ConfirmSignUp";
import Login from "../Login";
import Logout from "../Logout";
import ForgotPassword from "../ForgotPassword";

import AppLayout from "@components/layout/zenith/AppLayout";
import { VIEW_TRANSACTIONS_PERMISSIONS } from "@config/zenithPermissions";
import { useAppContext } from "@context/AppContext";
import Dashboard from "./dashboard/Dashboard";
import SchemeDashboard from "./scheme_dashboard/SchemeDashboard";
import ZoneAccount from "./zone_accounts/ZoneAccount";
import ZoneAccountSummary from "./zone_accounts/ZoneAccountSummary";
import WaterEntitlements from "./zone_accounts/WaterEntitlements";
import OfftakesAndMetersTab from "./zone_accounts/OfftakesAndMetersTab";
import TransactionHistory from "./zone_accounts/TransactionHistory";
import History from "./History";
import Notifications from "./notifications/Notifications";
import CreateNotification from "./notifications/CreateNotification";
import ExchangesDashboard from "./exchanges/ExchangesDashboard";
import WorkflowIndex from "./workflows/WorkflowIndex";
import WorkflowWaterDeclaration from "./workflows/WorkflowWaterDeclaration";
import AuditTrailIndex from "./audit_trail/AuditTrailIndex";
import WorkflowCreateForwardDraw from "./workflows/WorkflowCreateForwardDraw";
import WorkflowResponseApprovalInfoRequest from "./workflows/WorkflowResponseApprovalInfoRequest";

import Approvals, { ApprovalList } from "./approvals/Approvals";
import WorkflowCreateAdminTransfer from "./workflows/WorkflowCreateAdminTransfer";
import AccountBalances from "./account_balances/AccountBalances";
import Profile from "./profile/Profile";
import MyDetails from "./profile/MyDetails";
import SubscriberDetails from "./profile/SubscriberDetails";

// Opens its page to users holding `permissions` and sends anyone else to My
// Dashboard. It decides only once AppLayout has loaded the session: before
// that, every permission check fails.
const RequirePermissions: React.FunctionComponent<{
  permissions: string[];
  children: React.ReactElement;
}> = ({ permissions, children }) => {
  const { loggedInInfo, checkPermissions } = useAppContext();

  if (!loggedInInfo.isLoggedIn) return null;
  return checkPermissions(permissions) ? (
    children
  ) : (
    <Navigate to="/zenith" replace />
  );
};

const ZenithApp = () => {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<SchemeDashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="profile" element={<Profile />}>
          <Route path="me" element={<MyDetails />} />
          <Route path="subscriber" element={<SubscriberDetails />} />
          <Route index element={<Navigate to="/zenith/profile/me" />} />
        </Route>
        <Route path="zone_accounts/:id" element={<ZoneAccount />}>
          <Route index element={<Navigate to="summary" replace />} />
          <Route path="summary" element={<ZoneAccountSummary />} />
          <Route path="entitlements" element={<WaterEntitlements />} />
          <Route path="offtakes" element={<OfftakesAndMetersTab />} />
          <Route
            path="transactions"
            element={
              <RequirePermissions permissions={VIEW_TRANSACTIONS_PERMISSIONS}>
                <TransactionHistory />
              </RequirePermissions>
            }
          />
        </Route>
        <Route path="history" element={<History />} />
        <Route path="notifications">
          <Route path="new" element={<CreateNotification />} />
          <Route index element={<Notifications />} />
        </Route>
        <Route path="exchanges" element={<ExchangesDashboard />} />
        <Route path="workflows">
          <Route
            path="water_declaration"
            element={<WorkflowWaterDeclaration />}
          />
          <Route
            path="create_forward_draw"
            element={<WorkflowCreateForwardDraw />}
          />
          <Route
            path="create_admin_transfer"
            element={<WorkflowCreateAdminTransfer />}
          />
          <Route
            path="response_approval_info_request"
            element={<WorkflowResponseApprovalInfoRequest />}
          />
          <Route index element={<WorkflowIndex />} />
        </Route>
        <Route
          path="audit_trail"
          element={
            <RequirePermissions permissions={VIEW_TRANSACTIONS_PERMISSIONS}>
              <AuditTrailIndex />
            </RequirePermissions>
          }
        />
        <Route path="balances" element={<AccountBalances />} />

        <Route path="approvals" element={<Approvals />}>
          <Route index element={<Navigate to="seasonals" replace />} />
          <Route path=":tab" element={<ApprovalList />} />
        </Route>
      </Route>

      <Route path="logout" element={<Logout />} />
      <Route path="forgotPassword" element={<ForgotPassword />} />
      <Route element={<AuthTabs />}>
        <Route path="login" element={<Login />} />
        <Route path="signup" element={<SignUp />} />
      </Route>
      <Route path="confirm_signup" element={<ConfirmSignUp />} />

      <Route path="*" element={<Navigate to="/zenith" />} />
    </Routes>
  );
};

export default ZenithApp;
